<template>
	<div class="rb-player-bar">
		<div class="button" :class="{ disabled: !video.canPlay }">
			<transition name="fade">
				<span v-show="!video.playing">
					<rb-icon>play</rb-icon>
				</span>
			</transition>
			<transition name="fade">
				<span v-show="video.playing">
					<rb-icon>pause</rb-icon>
				</span>
			</transition>
		</div>

		<div class="label">
			{{ positionStr }} / {{ durationStr }}
		</div>

		<rb-player-timeline
			:video="video"
			:cues="cues"
			class="timeline"
		></rb-player-timeline>

		<div class="button" @click.stop>
			<rb-icon>cog</rb-icon>

			<div class="button-menu">
				<div class="menu left">

					<div class="item" @click="settings.skipOpening = !settings.skipOpening">
						Pular Abertura
						<div class="check-icon" :class="{ checked: settings.skipOpening }"></div>
					</div>

					<div class="item" @click="settings.skipEnding = !settings.skipEnding">
						Pular Encerramento
						<div class="check-icon" :class="{ checked: settings.skipEnding }"></div>
					</div>

					<div class="item"
						 v-for="source in sources"
						 :key="source.quality.name"
						 :class="{active: video.currentSource === source}"
						 @click="video.swapSource(source)">
						<sup>
							{{ source.quality.size }}p
						</sup>
						<span>
							{{ source.quality.name }}
						</span>
						<div class="check-icon" :class="{ checked: video.currentSource === source }"></div>
					</div>
				</div>
			</div>
		</div>

		<div class="button" @click.stop="onFullscreenClick">
			<rb-icon>compress</rb-icon>
		</div>

		<div class="button" @click.stop="onCastClick" v-if="video.castingEnabled">
			<rb-icon brand :style="{opacity: video.casting ? 1 : .5}">chromecast</rb-icon>
		</div>

		<div class="button" @click.stop="onVolumeClick">
			<transition name="fade">
				<span v-show="video.muted">
					<rb-icon>volume-mute</rb-icon>
				</span>
			</transition>
			<transition name="fade">
				<span v-show="!video.muted && video.volume > .5">
					<rb-icon>volume-up</rb-icon>
				</span>
			</transition>
			<transition name="fade">
				<span v-show="!video.muted && video.volume <= .5 && video.volume > 0">
					<rb-icon>volume-down</rb-icon>
				</span>
			</transition>
			<transition name="fade">
				<span v-show="!video.muted && video.volume === 0">
					<rb-icon>volume-off</rb-icon>
				</span>
			</transition>



			<div class="button-menu menu-volume"
				 @mousedown.stop.prevent="onVolumeBarMouseDown"
				 @mousemove="onVolumeBarMouseMove"
				 @mouseup="onVolumeBarMouseUp"
				 @mouseleave="onVolumeBarMouseLeave"
				 @click.stop.prevent
				 @dblclick.stop.prevent>
				<div class="menu left">
					<div class="item" style="width: 120px; height: 20px;">
						<sup>{{ Math.floor(video.volume*100) }}%</sup>
						<rb-player-timeline-bar
							:start="0"
							:end="100"
							:duration="100"
							class="bar-base"
						></rb-player-timeline-bar>
						<rb-player-timeline-bar
							:start="100 - (video.volume * 100)"
							:end="100"
							:duration="100"
							class="bar-volume"
						></rb-player-timeline-bar>
						<rb-player-timeline-bar
							:start="0"
							:end="100"
							:duration="100"
							class="click-area"
							ref="clickArea"
						></rb-player-timeline-bar>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import {secondsToTimestamp} from '../util/secondsToTimestamp';
	import {VideoPlayer} from '../VideoPlayer';
	import RbPlayerTimeline from './RbPlayerTimeline';
	import RbPlayerTimelineBar from './RbPlayerTimelineBar';
	import {VideoSettings} from '../VideoSettings';

	export default {
		name: 'RbPlayerBar',
		components: {RbPlayerTimelineBar, RbPlayerTimeline},
		props: {
			sources: {
				type: Array,
				required: true
			},
			cues: {
				type: Array,
				required: true
			},
			video: {
				type: VideoPlayer,
				required: true
			}
		},
		data: () => ({
			volumeSeeking: false,
			volumeSeekTimeout: null,
			settings: VideoSettings,
		}),
		computed: {
			positionStr() {
				return secondsToTimestamp(this.video.position);
			},
			durationStr() {
				return secondsToTimestamp(this.video.duration);
			}
		},
		methods: {
			onVolumeClick() {
				this.video.toggleMute();
			},
			_volumeFromEvent(e) {
				let rect = this.$refs.clickArea.$el.getBoundingClientRect();
				let x = e.clientX - rect.left;
				return (x / rect.width);
			},
			_updateVolume(e) {
				this.video.setVolume(1 - this._volumeFromEvent(e));
			},
			onVolumeBarMouseDown(e) {
				this._updateVolume(e);
				this.volumeSeeking = true;
			},
			onVolumeBarMouseMove(e) {
				if (this.volumeSeeking) {
					if (this.volumeSeekTimeout) {
						clearTimeout(this.volumeSeekTimeout);
						this.volumeSeekTimeout = null;
					}

					let v = this._volumeFromEvent(e);

					if (Math.abs(this.video.volume - v) > .1) {
						this._updateVolume(e);
					} else {
						this.volumeSeekTimeout = setTimeout(() => {
							this._updateVolume(e);
						}, 30);
					}
				}
			},
			onVolumeBarMouseUp(e) {
				this._updateVolume(e);
				this.volumeSeeking = false;
			},
			onVolumeBarMouseLeave() {
				this.volumeSeeking = false;
			},
			onFullscreenClick() {
				this.$emit('fullscreen');
			},
			chooseSource(source) {
				this.$emit('source', source);
			},
			onCastClick() {
				this.$emit('cast');
			}
		}
	}
</script>

<style lang="scss" src="./RbPlayerBar.scss"></style>
